// CLASSIC LOOK (v1.70): pre-Floodlight copy from ea44aec, used by
// screens/classic/CommunityScreen.jsx. Keep behaviour in step with
// components/community/EmbedPreview.jsx (the Floodlight version).
import { memo, useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { card, row, clipContent } from "../../../constants/commonStyles";
import { theme } from "../../../constants/theme";

// ─── URL parser ───────────────────────────────────────────────────────────────

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?.*)?$/i;

function parseUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.replace(/^www\./, "");

    // Direct image link
    if (IMAGE_EXT_RE.test(u.pathname)) {
      const filename = u.pathname.split("/").pop().split("?")[0];
      const ext = filename.split(".").pop().toUpperCase();
      return {
        platform: "image",
        label: host,
        filename,
        fileExt: ext,
        accentColor: theme.colors.borderStrong,
        iconName: "image-outline",
        description: host,
      };
    }

    // YouTube
    if (host === "youtube.com" || host === "youtu.be") {
      const videoId =
        host === "youtu.be"
          ? u.pathname.slice(1).split("?")[0]
          : u.searchParams.get("v");
      if (videoId) {
        return {
          platform: "youtube",
          label: "YouTube",
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          thumbnailFallback: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
          accentColor: "#FF0000",
          iconName: "logo-youtube",
          description: `youtu.be/${videoId}`,
        };
      }
    }

    // Twitch
    if (host === "twitch.tv") {
      const channel = u.pathname.slice(1).split("/").filter(Boolean)[0] ?? "";
      return {
        platform: "twitch",
        label: "Twitch",
        title: channel || null,
        thumbnail: null,
        accentColor: "#9147FF",
        iconName: "logo-twitch",
        description: channel ? `twitch.tv/${channel}` : "twitch.tv",
      };
    }

    // Twitter / X
    if (host === "twitter.com" || host === "x.com") {
      return {
        platform: "twitter",
        label: host === "x.com" ? "X (Twitter)" : "Twitter",
        thumbnail: null,
        accentColor: "#1DA1F2",
        iconName: "logo-twitter",
        description: rawUrl,
      };
    }

    // Generic
    return {
      platform: "generic",
      label: host,
      thumbnail: null,
      accentColor: theme.colors.borderStrong,
      iconName: "link-outline",
      description: rawUrl,
    };
  } catch {
    return null;
  }
}

// ─── ImageDisplay sub-component ───────────────────────────────────────────────

/**
 * Renders a direct image URL at its natural aspect ratio.
 * Calls onSizeLoaded({ width, height }) once the image dimensions are known.
 */
const ImageDisplay = memo(function ImageDisplay({ uri, onSizeLoaded }) {
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const handleLoad = useCallback(
    (e) => {
      const { width, height } = e.nativeEvent.source;
      setSize({ width, height });
      setLoading(false);
      onSizeLoaded?.({ width, height });
    },
    [onSizeLoaded]
  );

  const handleError = useCallback(() => {
    setFailed(true);
    setLoading(false);
  }, []);

  if (failed) return null;

  return (
    // Placeholder container while loading; collapses once the image defines its own height.
    <View style={[styles.imageContainer, !size && styles.imageContainerPlaceholder]}>
      <Image
        source={{ uri }}
        style={[styles.imageFill, !!size && { aspectRatio: size.width / size.height }]}
        resizeMode="contain"
        onLoad={handleLoad}
        onError={handleError}
      />
      {loading && (
        <View style={[StyleSheet.absoluteFill, styles.imageLoadingOverlay]}>
          <ActivityIndicator size="small" color={theme.colors.textTertiary} />
        </View>
      )}
    </View>
  );
});

// ─── Thumbnail sub-component (video platforms) ────────────────────────────────

const Thumbnail = memo(function Thumbnail({ uri, fallback }) {
  const [src, setSrc] = useState(uri);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const handleError = useCallback(() => {
    if (src === uri && fallback) {
      setSrc(fallback);
    } else {
      setFailed(true);
      setLoading(false);
    }
  }, [src, uri, fallback]);

  if (failed) return null;

  return (
    <View style={styles.thumbContainer}>
      <Image
        source={{ uri: src }}
        style={styles.thumbnail}
        resizeMode="cover"
        onLoad={() => setLoading(false)}
        onError={handleError}
      />
      {loading && (
        <View style={styles.thumbLoading}>
          <ActivityIndicator size="small" color={theme.colors.textTertiary} />
        </View>
      )}
      {!loading && (
        <View style={styles.playOverlay} pointerEvents="none">
          <View style={styles.playBtn}>
            <Ionicons name="play" size={20} color="#fff" />
          </View>
        </View>
      )}
    </View>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * EmbedPreview
 *
 * Renders a rich link-preview card for an external URL or video URL.
 * Detects: direct image files, YouTube, Twitch, Twitter/X, and generic links.
 *
 * Props:
 *   url    {string}  The URL to preview (required)
 *   style  {object}  Optional extra style for the outer container
 */
const EmbedPreview = memo(function EmbedPreview({ url, style }) {
  const meta = useMemo(() => parseUrl(url), [url]);
  const [imageSize, setImageSize] = useState(null);

  const handlePress = useCallback(() => {
    if (url) Linking.openURL(url).catch(() => {});
  }, [url]);

  if (!meta) return null;

  const isImage = meta.platform === "image";

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.75}
      style={[styles.card, { borderLeftColor: meta.accentColor }, style]}
      accessibilityRole="link"
      accessibilityLabel={`Open ${meta.label} link`}
    >
      {/* Direct image display */}
      {isImage && (
        <ImageDisplay uri={url} onSizeLoaded={setImageSize} />
      )}

      {/* Video thumbnail (YouTube etc.) */}
      {!isImage && !!meta.thumbnail && (
        <Thumbnail uri={meta.thumbnail} fallback={meta.thumbnailFallback} />
      )}

      {/* Metadata section */}
      <View style={styles.metaSection}>
        {/* Platform / source badge */}
        <View style={[row, { gap: 6, marginBottom: 4 }]}>
          <Ionicons name={meta.iconName} size={13} color={meta.accentColor} />
          <Text style={[styles.platformLabel, { color: meta.accentColor }]}>
            {meta.label}
          </Text>
          {/* File-type badge for images */}
          {isImage && !!meta.fileExt && (
            <View style={styles.extBadge}>
              <Text style={styles.extBadgeText}>{meta.fileExt}</Text>
            </View>
          )}
        </View>

        {/* Filename (images) or channel title (Twitch) */}
        {!!(meta.filename ?? meta.title) && (
          <Text style={styles.title} numberOfLines={1}>
            {meta.filename ?? meta.title}
          </Text>
        )}

        {/* Image dimensions — shown once the image has loaded */}
        {isImage && !!imageSize && (
          <Text style={styles.imageDimensions}>
            {imageSize.width} × {imageSize.height} px
          </Text>
        )}

        {/* URL / description */}
        {!isImage && (
          <Text style={styles.description} numberOfLines={2}>
            {meta.description}
          </Text>
        )}

        {/* External link hint */}
        <View style={[row, { gap: 4, marginTop: 6 }]}>
          <Ionicons name="open-outline" size={11} color={theme.colors.textTertiary} />
          <Text style={styles.openHint}>Open link</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

export default EmbedPreview;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    ...card,
    ...clipContent,
    marginTop: 6,
    borderLeftWidth: 3,
    maxWidth: 360,
  },

  // ── Direct image ──────────────────────────────────────────────────
  imageContainer: {
    width: "100%",
    backgroundColor: theme.colors.backgroundAlt,
  },
  imageContainerPlaceholder: {
    height: 140,
  },
  imageFill: {
    width: "100%",
  },
  imageLoadingOverlay: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.backgroundAlt,
  },

  // ── Video thumbnail ───────────────────────────────────────────────
  thumbContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    backgroundColor: theme.colors.backgroundAlt,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  thumbLoading: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.backgroundAlt,
  },
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
  },
  playBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "rgba(0,0,0,0.55)",
    alignItems: "center",
    justifyContent: "center",
  },

  // ── Metadata ──────────────────────────────────────────────────────
  metaSection: {
    padding: 10,
  },
  platformLabel: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  extBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 3,
    backgroundColor: theme.colors.glass,
    borderWidth: 1,
    borderColor: theme.colors.borderFaint,
  },
  extBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    color: theme.colors.textTertiary,
    letterSpacing: 0.3,
  },
  title: {
    fontSize: 14,
    fontWeight: "600",
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  imageDimensions: {
    fontSize: 11,
    color: theme.colors.textTertiary,
    marginTop: 1,
  },
  description: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    lineHeight: 17,
  },
  openHint: {
    fontSize: 11,
    color: theme.colors.textTertiary,
  },
});
